import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { Database, withLogs } from '@core';

export type UserPreferredUnit = 'metric' | 'imperial' | 'mixed';
export const USER_SEX = {
    MALE: 'M',
    FEMALE: 'F',
    OTHER: 'O',
};
export type UserSex = 'M' | 'F' | 'O';

export const LANGUAGE_MAP = {
    en: 'English',
    is: 'Íslenska',
};
export type LocaleCode = keyof typeof LANGUAGE_MAP;
export const languages: LocaleCode[] = ['en', 'is'];

export type UserSettings = {
    dateOfBirth: string | null;
    sex: UserSex | null;
    handicap: number;
    userId: string;
    userPreferredUnit: UserPreferredUnit;
    locale: LocaleCode;
    showCorridors: boolean;
    showGoal: boolean;
    liveView: boolean;
    leftHanded: boolean;
    comparisonSwingId: string | null;
    showComparison: boolean;
};

export const fetchUserSettings = withLogs(async (supabase: SupabaseClient<Database>, userId: string) => {
    const { data, error } = await supabase.from('user_settings').select('*').eq('user_id', userId).maybeSingle();

    if (error) throw error;

    if (!data) {
        return null;
    }

    return camelcaseKeys({ ...data, dateOfBirth: data.dob }) as UserSettings;
}, 'fetchUserSettings');

export const fetchUserSettingsWithThrow = withLogs(async (supabase: SupabaseClient<Database>, userId: string) => {
    const { data, error } = await supabase.from('user_settings').select('*').eq('user_id', userId).maybeSingle();

    if (error) throw error;

    if (!data) {
        return null;
    }

    return camelcaseKeys({ ...data, dateOfBirth: data.dob }) as UserSettings;
}, 'fetchUserSettingsWithThrow');

export const upsertUserSettings = withLogs(
    async (supabase: SupabaseClient<Database>, userSettings: Pick<UserSettings, 'userId'> & Partial<UserSettings>) => {
        const { dateOfBirth, ...partial } = userSettings;
        const payload = snakecaseKeys(partial);
        const { data, error } = await supabase
            .from('user_settings')
            .upsert({ ...payload, dob: dateOfBirth })
            .select('*')
            .maybeSingle();

        if (error) throw error;

        return data ? camelcaseKeys(data) : null;
    },
    'upsertUserSettings',
);

export const subscribeToUserSettings = (
    supabase: SupabaseClient<Database>,
    userId: string,
    handler: (payload: UserSettings) => Promise<void> | void,
) => {
    return supabase
        .channel('user_settings')
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'user_settings',
                filter: `user_id=eq.${userId}`,
            },
            async (payload) => {
                try {
                    const userSettings = payload.new as any;
                    await handler(camelcaseKeys(userSettings));
                } catch (error) {
                    console.error('Error processing change:', error);
                }
            },
        )
        .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
                console.error('user settings subscription error:', status);
            }
        });
};
